import { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ShoppingBag, MapPin, Heart, Loader2, Check, Leaf, Hash, Star } from 'lucide-react';
import { toast } from 'sonner';
import { InventoryBadge, InventoryOverlay } from './InventoryBadge';
import { PLUBadge } from './PLUBadge';
import { useLanguage } from '../../contexts/LanguageContext';

// Product card translations
const PRODUCT_TRANSLATIONS = {
  en: {
    soldOut: 'Sold out',
    available: 'available',
    adding: 'Adding...',
    added: 'Added',
    addToCart: 'Add to Cart'
  },
  es: {
    soldOut: 'Agotado',
    available: 'disponible',
    adding: 'Agregando...',
    added: 'Agregado',
    addToCart: 'Agregar'
  },
  fr: {
    soldOut: 'Épuisé',
    available: 'disponible',
    adding: 'Ajout...',
    added: 'Ajouté',
    addToCart: 'Ajouter'
  },
  de: {
    soldOut: 'Ausverkauft',
    available: 'verfügbar',
    adding: 'Hinzufügen...',
    added: 'Hinzugefügt',
    addToCart: 'Hinzufügen'
  },
  it: {
    soldOut: 'Esaurito',
    available: 'disponibile',
    adding: 'Aggiunta...',
    added: 'Aggiunto',
    addToCart: 'Aggiungi'
  },
  pt: {
    soldOut: 'Esgotado',
    available: 'disponível',
    adding: 'Adicionando...',
    added: 'Adicionado',
    addToCart: 'Adicionar'
  },
  ar: {
    soldOut: 'نفذ',
    available: 'متاح',
    adding: 'جارٍ الإضافة...',
    added: 'تمت الإضافة',
    addToCart: 'أضف'
  },
  zh: {
    soldOut: '售罄',
    available: '可用',
    adding: '添加中...',
    added: '已添加',
    addToCart: '添加'
  },
  ko: {
    soldOut: '품절',
    available: '개 가능',
    adding: '추가 중...',
    added: '추가됨',
    addToCart: '추가'
  }
};

/**
 * ProductCard - Unified product/deal card component
 * 
 * Consolidates duplicated product card code from:
 * - ConsumerBrowse.js
 * - ConsumerDashboard.js
 * - PersonalizedRecommendations.js
 * - FoodAssistant.js
 * - GroceryListPlanner.js
 * - ConsumerRadar.js
 * 
 * @param {Object} props
 * @param {Object} props.item - The product/deal item
 * @param {Function} props.onAddToCart - Callback when adding to cart
 * @param {Object} props.inventoryStatus - Real-time inventory status (optional)
 * @param {boolean} props.hasRecentUpdate - Whether item has recent inventory update
 * @param {boolean} props.showStoreName - Whether to show retailer name
 * @param {boolean} props.showPLU - Whether to show PLU badge for produce
 * @param {boolean} props.compact - Use compact layout
 * @param {string} props.variant - 'default' | 'featured' | 'recommendation' | 'nlu' | 'fulfillment'
 * @param {string} props.reason - Recommendation reason to display
 * @param {boolean} props.isAdded - Whether item is already in cart
 * @param {boolean} props.isAdding - Whether item is being added
 * @param {number} props.matchScore - AI match score (0-100) for NLU variant
 * @param {number} props.discountLevel - Discount level (1-3) for color coding
 * @param {string} props.highlightText - Text to highlight in name/description (for search)
 */

// Helper function to highlight text
function HighlightText({ text, highlight }) {
  if (!highlight || !text) return <>{text}</>;
  
  try {
    const escapedHighlight = highlight.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const parts = text.split(new RegExp(`(${escapedHighlight})`, 'gi'));
    
    return (
      <>
        {parts.map((part, i) => 
          part.toLowerCase() === highlight.toLowerCase() ? (
            <mark key={i} className="bg-yellow-200 text-yellow-900 px-0.5 rounded">{part}</mark>
          ) : (
            <span key={i}>{part}</span>
          )
        )}
      </>
    );
  } catch {
    return <>{text}</>;
  }
}

export default function ProductCard({
  item,
  onAddToCart,
  inventoryStatus,
  hasRecentUpdate = false,
  showStoreName = true,
  showPLU = true,
  compact = false,
  variant = 'default',
  reason,
  isAdded = false,
  isAdding = false,
  matchScore,
  discountLevel,
  highlightText = '',
  className = ''
}) {
  const [localAdding, setLocalAdding] = useState(false);
  
  // Get current language for translations
  const { locale } = useLanguage();
  const pt = PRODUCT_TRANSLATIONS[locale] || PRODUCT_TRANSLATIONS.en;

  // Normalize item properties (different APIs use different field names)
  const id = item.id || item.rshd_id || item.item_id;
  const name = item.name || item.item_name || 'Unknown Item';
  const description = item.description || '';
  const category = item.category || 'Miscellaneous';
  const dealPrice = item.deal_price || item.price || 0;
  const regularPrice = item.regular_price || item.original_price || dealPrice * 1.5;
  const discountPercent = item.consumer_discount_percent || item.discount_percentage || 
    Math.round((1 - dealPrice / regularPrice) * 100);
  const quantity = inventoryStatus?.quantity ?? item.quantity ?? 0;
  const imageUrl = item.image_url || item.imageUrl;
  const storeName = item.drlp_name || item.store_name || item.retailer_name || '';
  const isOrganic = item.is_organic || item.isOrganic || name.toLowerCase().includes('organic');
  const isVegan = item.is_vegan || item.isVegan;
  const isGlutenFree = item.is_gluten_free || item.isGlutenFree;
  
  // Inventory status
  const isSoldOut = inventoryStatus?.isSoldOut || quantity <= 0;
  const isLowStock = inventoryStatus?.isLowStock || (quantity > 0 && quantity <= 5);

  // Dietary tags
  const dietaryTags = item.dietary_tags || [];
  const isHalal = dietaryTags.includes('halal') || item.is_halal;
  const isKosher = dietaryTags.includes('kosher') || item.is_kosher;

  // Match score for NLU variant
  const itemMatchScore = matchScore ?? item.match_score ?? item.relevance_score;
  
  // Discount level for color coding
  const itemDiscountLevel = discountLevel ?? item.discount_level ?? 1;

  const handleAddToCart = async () => {
    if (isSoldOut || isAdding || localAdding) return;
    
    setLocalAdding(true);
    try {
      if (onAddToCart) {
        await onAddToCart(item);
      }
    } catch (error) {
      toast.error('Failed to add to cart');
    } finally {
      setLocalAdding(false);
    }
  };

  const adding = isAdding || localAdding;

  // Get discount badge color based on level
  const getDiscountBadgeColor = (level) => {
    switch(level) {
      case 1: return 'bg-green-500';
      case 2: return 'bg-yellow-500';
      case 3: return 'bg-red-500';
      default: return 'bg-emerald-500';
    }
  };

  const getDiscountLabel = (level) => {
    switch(level) {
      case 1: return '50% OFF';
      case 2: return '60% OFF';
      case 3: return '75% OFF';
      default: return `${discountPercent}% OFF`;
    }
  };

  // Compact layout for sidebars/lists
  if (compact) {
    return (
      <Card 
        className={`overflow-hidden hover:shadow-md transition-all ${isSoldOut ? 'opacity-60' : ''} ${className}`}
        data-testid={`product-card-${id}`}
      >
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            {/* Thumbnail */}
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
              {imageUrl ? (
                <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
              ) : (
                <ShoppingBag className="w-8 h-8 text-emerald-400" />
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-sm line-clamp-1">
                <HighlightText text={name} highlight={highlightText} />
              </h4>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-emerald-600 font-bold">${dealPrice.toFixed(2)}</span>
                <span className="text-xs text-gray-400 line-through">${regularPrice.toFixed(2)}</span>
                <Badge className="bg-red-500 text-white text-xs px-1.5 py-0">{discountPercent}%</Badge>
              </div>
              {reason && (
                <p className="text-xs text-purple-600 mt-1 line-clamp-1">{reason}</p>
              )}
            </div>
            
            {/* Add Button */}
            <Button
              size="sm"
              onClick={handleAddToCart}
              disabled={isSoldOut || adding || isAdded}
              className={`flex-shrink-0 ${
                isAdded ? 'bg-green-500' : 
                isSoldOut ? 'bg-gray-400' : 
                'bg-emerald-600 hover:bg-emerald-700'
              }`}
              data-testid={`add-to-cart-compact-${id}`}
            >
              {adding ? <Loader2 className="w-4 h-4 animate-spin" /> :
               isAdded ? <Check className="w-4 h-4" /> :
               isSoldOut ? 'Out' : '+'}
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // NLU variant - For FoodAssistant AI search results with match score
  if (variant === 'nlu') {
    return (
      <Card 
        className={`overflow-hidden hover:shadow-lg transition-all ${isSoldOut ? 'opacity-60' : ''} ${className}`}
        data-testid={`product-card-nlu-${id}`}
      >
        {/* Discount Level Header */}
        <div className={`${getDiscountBadgeColor(itemDiscountLevel)} text-white text-center py-1.5 text-sm font-bold flex items-center justify-center gap-2`}>
          <span>{getDiscountLabel(itemDiscountLevel)}</span>
          {itemMatchScore && (
            <span className="bg-white/20 px-2 py-0.5 rounded-full text-xs flex items-center gap-1">
              <Star className="w-3 h-3" /> {Math.round(itemMatchScore)}% match
            </span>
          )}
        </div>
        
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            {/* Thumbnail */}
            <div className="w-16 h-16 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
              {imageUrl ? (
                <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
              ) : (
                <ShoppingBag className="w-8 h-8 text-emerald-400" />
              )}
            </div>
            
            {/* Info */}
            <div className="flex-1 min-w-0">
              <h4 className="font-bold text-gray-900 line-clamp-1">
                <HighlightText text={name} highlight={highlightText} />
              </h4>
              
              {/* Store & Category */}
              <div className="flex items-center gap-2 text-xs text-gray-500 mt-1">
                {showStoreName && storeName && (
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3" />
                    {storeName}
                  </span>
                )}
                <Badge variant="outline" className="text-xs">{category}</Badge>
              </div>
              
              {/* Dietary Tags */}
              <div className="flex flex-wrap gap-1 mt-2">
                {isOrganic && (
                  <Badge className="bg-green-100 text-green-700 text-xs">
                    <Leaf className="w-3 h-3 mr-0.5" />Organic
                  </Badge>
                )}
                {isHalal && <Badge className="bg-emerald-100 text-emerald-700 text-xs">Halal</Badge>}
                {isKosher && <Badge className="bg-blue-100 text-blue-700 text-xs">Kosher</Badge>}
                {isVegan && <Badge className="bg-lime-100 text-lime-700 text-xs">Vegan</Badge>}
                {isGlutenFree && <Badge className="bg-amber-100 text-amber-700 text-xs">GF</Badge>}
              </div>
              
              {/* Price Row */}
              <div className="flex items-center justify-between mt-3">
                <div className="flex items-baseline gap-2">
                  <span className="text-xl font-bold text-emerald-600">${dealPrice.toFixed(2)}</span>
                  <span className="text-sm text-gray-400 line-through">${regularPrice.toFixed(2)}</span>
                </div>
                
                <Button
                  size="sm"
                  onClick={handleAddToCart}
                  disabled={isSoldOut || adding || isAdded}
                  className={`${
                    isAdded ? 'bg-green-500' : 
                    isSoldOut ? 'bg-gray-400' : 
                    'bg-emerald-600 hover:bg-emerald-700'
                  }`}
                  data-testid={`add-to-cart-nlu-${id}`}
                >
                  {adding ? <Loader2 className="w-4 h-4 animate-spin" /> :
                   isAdded ? <><Check className="w-4 h-4 mr-1" /> Added</> :
                   isSoldOut ? 'Sold Out' : '+ Add'}
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Fulfillment variant - For GroceryListPlanner grouped items
  if (variant === 'fulfillment') {
    return (
      <div 
        className={`flex items-center gap-3 py-3 border-b last:border-b-0 ${isSoldOut ? 'opacity-50' : ''}`}
        data-testid={`product-card-fulfillment-${id}`}
      >
        {/* Small Image */}
        <div className="w-12 h-12 bg-gradient-to-br from-emerald-100 to-teal-100 rounded-lg flex-shrink-0 flex items-center justify-center overflow-hidden">
          {imageUrl ? (
            <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
          ) : (
            <ShoppingBag className="w-6 h-6 text-emerald-400" />
          )}
        </div>
        
        {/* Name and Tags */}
        <div className="flex-1 min-w-0">
          <h4 className="font-medium text-sm line-clamp-1">
            <HighlightText text={name} highlight={highlightText} />
          </h4>
          <div className="flex items-center gap-1 mt-0.5">
            {isOrganic && (
              <Badge className="bg-green-100 text-green-700 text-xs py-0">
                <Leaf className="w-2.5 h-2.5 mr-0.5" />Organic
              </Badge>
            )}
            {quantity > 0 && quantity <= 5 && (
              <span className="text-xs text-yellow-600">{quantity} left</span>
            )}
          </div>
        </div>
        
        {/* Price */}
        <div className="text-right flex-shrink-0">
          <span className="font-bold text-emerald-600">${dealPrice.toFixed(2)}</span>
          <span className="text-xs text-gray-400 line-through ml-1">${regularPrice.toFixed(2)}</span>
        </div>
        
        {/* Action */}
        <Button
          size="sm"
          variant="ghost"
          onClick={handleAddToCart}
          disabled={isSoldOut || adding || isAdded}
          className={`flex-shrink-0 h-8 w-8 p-0 ${
            isAdded ? 'text-green-500' : 
            isSoldOut ? 'text-gray-400' : 
            'text-emerald-600 hover:bg-emerald-50'
          }`}
          data-testid={`add-to-cart-fulfillment-${id}`}
        >
          {adding ? <Loader2 className="w-4 h-4 animate-spin" /> :
           isAdded ? <Check className="w-4 h-4" /> :
           isSoldOut ? '—' : '+'}
        </Button>
      </div>
    );
  }

  // Full card layout
  return (
    <Card
      className={`overflow-hidden hover:shadow-xl transition-all duration-300 relative ${
        isSoldOut ? 'opacity-75' : ''
      } ${variant === 'featured' ? 'ring-2 ring-amber-400' : ''} ${className}`}
      data-testid={`product-card-${id}`}
    >
      {/* Sold Out Overlay */}
      {isSoldOut && <InventoryOverlay isSoldOut={true} />}
      
      {/* Image Section */}
      <div className="aspect-square bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center relative">
        {imageUrl ? (
          <img src={imageUrl} alt={name} className="w-full h-full object-cover" />
        ) : (
          <ShoppingBag className="w-20 h-20 text-emerald-400" />
        )}
        
        {/* Discount Badge */}
        <Badge className="absolute top-3 right-3 bg-red-500 text-white font-bold">
          {discountPercent}% OFF
        </Badge>
        
        {/* Featured Badge */}
        {variant === 'featured' && (
          <Badge className="absolute top-3 left-3 bg-amber-500 text-white">
            ⭐ Featured
          </Badge>
        )}
        
        {/* Inventory Badge */}
        {(isLowStock || hasRecentUpdate) && !isSoldOut && (
          <div className="absolute bottom-3 left-3">
            <InventoryBadge 
              quantity={quantity} 
              status={inventoryStatus?.status || (isLowStock ? 'low' : 'available')}
              hasLiveUpdate={hasRecentUpdate}
              showQuantity={isLowStock}
            />
          </div>
        )}
      </div>
      
      {/* Content Section */}
      <CardContent className="p-4">
        {/* Category & Tags Row */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <Badge variant="outline" className="text-xs">
            {category}
          </Badge>
          
          {/* PLU Badge for produce */}
          {showPLU && (category === 'Fruits' || category === 'Vegetables') && (
            <PLUBadge itemName={name} isOrganic={isOrganic} />
          )}
          
          {/* Dietary Tags */}
          {isOrganic && (
            <Badge className="bg-green-100 text-green-700 text-xs">
              <Leaf className="w-3 h-3 mr-1" />
              Organic
            </Badge>
          )}
          {isHalal && (
            <Badge className="bg-emerald-100 text-emerald-700 text-xs">Halal</Badge>
          )}
          {isKosher && (
            <Badge className="bg-blue-100 text-blue-700 text-xs">Kosher</Badge>
          )}
        </div>
        
        {/* Name & Description */}
        <h3 className="font-bold text-gray-900 mb-1 line-clamp-1" data-testid={`product-name-${id}`}>
          <HighlightText text={name} highlight={highlightText} />
        </h3>
        {description && (
          <p className="text-sm text-gray-600 mb-1 line-clamp-1">
            <HighlightText text={description} highlight={highlightText} />
          </p>
        )}
        
        {/* Store Name */}
        {showStoreName && storeName && (
          <div className="flex items-center text-xs text-gray-500 mb-3">
            <MapPin className="w-3 h-3 mr-1" />
            {storeName}
          </div>
        )}
        
        {/* Recommendation Reason */}
        {reason && (
          <p className="text-xs text-purple-600 mb-2 line-clamp-1 bg-purple-50 px-2 py-1 rounded">
            💡 {reason}
          </p>
        )}
        
        {/* Price Row */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="text-2xl font-bold text-emerald-600">${dealPrice.toFixed(2)}</span>
            <span className="text-sm text-gray-500 line-through">${regularPrice.toFixed(2)}</span>
          </div>
        </div>
        
        {/* Action Row */}
        <div className="flex items-center justify-between">
          <span className={`text-xs ${isLowStock ? 'text-yellow-600 font-medium' : 'text-gray-500'}`}>
            {isSoldOut ? pt.soldOut : `${quantity} ${pt.available}`}
            {hasRecentUpdate && <span className="ml-1 text-green-500">•</span>}
          </span>
          
          <Button
            size="sm"
            onClick={handleAddToCart}
            disabled={isSoldOut || adding || isAdded}
            className={`${
              isAdded ? 'bg-green-500 hover:bg-green-600' :
              isSoldOut ? 'bg-gray-400 cursor-not-allowed' : 
              'bg-emerald-600 hover:bg-emerald-700'
            }`}
            data-testid={`add-to-cart-${id}`}
          >
            {adding ? (
              <Loader2 className="w-4 h-4 animate-spin mr-1" />
            ) : isAdded ? (
              <Check className="w-4 h-4 mr-1" />
            ) : null}
            {adding ? pt.adding : isAdded ? pt.added : isSoldOut ? pt.soldOut : pt.addToCart}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * ProductCardGrid - Grid container for product cards
 */
export function ProductCardGrid({ children, className = '' }) {
  return (
    <div className={`grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 ${className}`}>
      {children}
    </div>
  );
}

/**
 * ProductCardSkeleton - Loading placeholder
 */
export function ProductCardSkeleton({ compact = false }) {
  if (compact) {
    return (
      <Card className="animate-pulse">
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <div className="w-16 h-16 bg-gray-200 rounded-lg" />
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
              <div className="h-3 bg-gray-200 rounded w-1/2" />
            </div>
            <div className="w-10 h-8 bg-gray-200 rounded" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden animate-pulse">
      <div className="aspect-square bg-gray-200" />
      <CardContent className="p-4">
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-3" />
        <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-3 bg-gray-200 rounded w-1/2 mb-4" />
        <div className="flex justify-between items-center">
          <div className="h-6 bg-gray-200 rounded w-1/4" />
          <div className="h-8 bg-gray-200 rounded w-24" />
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * EmptyProductState - Empty state when no products found
 */
export function EmptyProductState({ message = 'No deals found', suggestion = 'Try adjusting your filters' }) {
  return (
    <Card>
      <CardContent className="py-12 text-center">
        <ShoppingBag className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-500 text-lg">{message}</p>
        {suggestion && <p className="text-gray-400 text-sm mt-2">{suggestion}</p>}
      </CardContent>
    </Card>
  );
}
